import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useApp } from '../context/AppContext';

const ProductRecommendations = ({ productId, productName }) => {
  const { t, API, isFavorite, toggleFavorite, token } = useApp();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    if (productId) {
      loadRecommendations();
    }
  }, [productId]);

  const loadRecommendations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${productId}/recommendations`);
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const scroll = (direction) => {
    const container = document.getElementById('recommendations-container');
    if (container) {
      const scrollAmount = 300;
      const newPosition = direction === 'left' 
        ? Math.max(0, scrollPosition - scrollAmount)
        : scrollPosition + scrollAmount;
      container.scrollTo({ left: newPosition, behavior: 'smooth' });
      setScrollPosition(newPosition);
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="flex justify-center">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="py-8 border-t border-[#2C1B18]" data-testid="product-recommendations">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading text-white">
          {t('customers_also_bought')}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => scroll('left')}
            className="p-2 glass rounded-full hover:bg-white/10 transition-colors"
            aria-label={t('previous')}
          >
            <ChevronLeft size={20} className="text-white" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="p-2 glass rounded-full hover:bg-white/10 transition-colors"
            aria-label={t('next')}
          >
            <ChevronRight size={20} className="text-white" />
          </button>
        </div>
      </div>

      <div 
        id="recommendations-container"
        className="flex gap-4 overflow-x-auto pb-4 scroll-smooth hide-scrollbar"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {recommendations.map((product) => (
          <Link
            key={product.id}
            to={`/product/${product.id}`}
            className="flex-shrink-0 w-[220px] glass rounded-xl overflow-hidden group hover:border-[#D4AF37]/30 border border-transparent transition-all"
            data-testid={`recommendation-${product.id}`}
          >
            {/* Image */}
            <div className="relative aspect-square bg-gradient-to-br from-[#2C1B18] to-[#1C1917] overflow-hidden">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              {/* Favorite Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (token) toggleFavorite(product.id);
                }}
                className="absolute top-3 right-3 p-2 rounded-full bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-colors"
              >
                <Heart 
                  size={16} 
                  className={isFavorite(product.id) ? 'fill-red-500 text-red-500' : 'text-white'} 
                />
              </button>
              {/* Recommendation Reason */}
              {product.recommendation_reason && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <p className="text-[#D4AF37] text-xs">{product.recommendation_reason}</p>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-4">
              <p className="text-[#D4AF37] text-xs tracking-wider mb-1">{product.brand}</p>
              <h3 className="text-white font-medium text-sm truncate">{product.name}</h3>
              <p className="text-[#57534E] text-xs mt-1">{product.category === 'men' ? t('men') : t('women')}</p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-white font-bold">
                  €{product.sizes?.[0]?.price?.toFixed(2) || product.price?.toFixed(2)}
                </span>
                {product.original_price && product.original_price > (product.sizes?.[0]?.price || product.price) && (
                  <span className="text-[#57534E] text-sm line-through">
                    €{product.original_price.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ProductRecommendations;
